(() => {
  if (window.__woQuantityPreselectionLoaded) return;
  window.__woQuantityPreselectionLoaded = true;

  const toInteger = (value, fallback = null) => {
    const parsed = Number.parseInt(value, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const getQuantityConfig = (productInfo) => {
    const source = productInfo?.querySelector('[data-wo-quantity-preselection]');
    if (!source) return { product: null, variants: {} };

    try {
      const parsed = JSON.parse(source.textContent || '{}');
      return {
        product: parsed?.product ?? null,
        variants: parsed?.variants && typeof parsed.variants === 'object' ? parsed.variants : {},
      };
    } catch (error) {
      console.error('Weinonline quantity preselection: invalid configuration', error);
      return { product: null, variants: {} };
    }
  };

  const getCurrentVariantId = (productInfo) => {
    const input = productInfo?.querySelector('.product-variant-id, product-form input[name="id"]');
    return input?.value ? String(input.value) : null;
  };

  const getPreferredQuantity = (productInfo, requestedVariantId = null) => {
    const config = getQuantityConfig(productInfo);
    const productQuantity = toInteger(config.product, null);

    // Product-level value is authoritative in the current Weinonline architecture.
    if (productQuantity !== null) return productQuantity;

    // Variant-level value remains as a safe fallback for legacy products that
    // have not yet received a product-level quantity value.
    const variantId = requestedVariantId != null ? String(requestedVariantId) : getCurrentVariantId(productInfo);
    return variantId && config.variants[variantId] != null ? toInteger(config.variants[variantId], null) : null;
  };

  const getBounds = (input) => {
    const cartQuantity = toInteger(input?.dataset.cartQuantity, 0);
    const ruleMin = toInteger(input?.dataset.min, 1);
    const ruleMax = input?.dataset.max != null ? toInteger(input.dataset.max, null) : null;
    const step = Math.max(toInteger(input?.step, 1), 1);

    let min = ruleMin;
    const max = ruleMax === null ? null : ruleMax - cartQuantity;

    if (max !== null) min = Math.min(min, max);
    if (cartQuantity >= ruleMin) min = Math.min(min, step);

    return { min, max, step };
  };

  const isValidQuantity = (quantity, bounds) => {
    if (!Number.isFinite(quantity) || quantity < bounds.min) return false;
    if (bounds.max !== null && quantity > bounds.max) return false;
    return (quantity - bounds.min) % bounds.step === 0;
  };

  const setInputBounds = (input, bounds) => {
    input.min = bounds.min;
    if (bounds.max !== null && bounds.max > 0) {
      input.max = bounds.max;
    } else {
      input.removeAttribute('max');
    }
  };

  const applyPreselection = (productInfo, requestedVariantId = null) => {
    if (!productInfo) return;

    const input = productInfo.querySelector('.quantity__input');
    if (!input) return;

    const preferred = getPreferredQuantity(productInfo, requestedVariantId);
    const bounds = getBounds(input);
    const quantity = isValidQuantity(preferred, bounds) ? preferred : bounds.min;

    setInputBounds(input, bounds);
    input.value = quantity;
    input.dispatchEvent(new Event('change', { bubbles: true }));

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
    }
  };

  const patchProductInfo = () => {
    const ProductInfo = customElements.get('product-info');
    const prototype = ProductInfo?.prototype;
    if (!prototype || prototype.__woQuantityPreselectionPatched) return;

    prototype.__woQuantityPreselectionPatched = true;

    // Dawn normally resets the field to its minimum quantity after product or
    // cart updates. Preserve a valid preselected/current value instead.
    prototype.setQuantityBoundries = function () {
      if (!this.quantityInput) return;

      const bounds = getBounds(this.quantityInput);
      const currentValue = toInteger(this.quantityInput.value, null);

      setInputBounds(this.quantityInput, bounds);
      if (!isValidQuantity(currentValue, bounds)) {
        this.quantityInput.value = bounds.min;
      }

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }
    };

    const originalUpdateQuantityRules = prototype.updateQuantityRules;
    if (typeof originalUpdateQuantityRules === 'function') {
      prototype.updateQuantityRules = function (sectionId, html) {
        originalUpdateQuantityRules.call(this, sectionId, html);
        this.setQuantityBoundries();
      };
    }
  };

  const run = () => {
    patchProductInfo();
    document.querySelectorAll('product-info').forEach((productInfo) => applyPreselection(productInfo));
  };

  document.addEventListener('product-info:loaded', (event) => {
    const productInfo = event.target?.matches?.('product-info') ? event.target : event.target?.closest?.('product-info');
    queueMicrotask(() => applyPreselection(productInfo));
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const productInfo = Array.from(document.querySelectorAll('product-info')).find(
        (element) => element.dataset.section === data?.sectionId || element.dataset.originalSection === data?.sectionId
      );
      queueMicrotask(() => applyPreselection(productInfo, data?.variant?.id));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
})();
